/**
 * Board Repository
 * Data access for boards
 * 
 * @module repositories/boardRepository
 */

import { supabase } from '../services/supabase';
import { ApiError } from '../lib/errors';
import { logger } from '../lib/logger';
import { getCurrentUserId } from '../services/authService';

// =============================================================================
// Types
// =============================================================================

export interface BoardRow {
    id: string;
    user_id: string;
    title: string;
    description: string | null;
    slug: string;
    icon: string | null;
    theme_color: string | null;
    is_favorite: boolean;
    is_shared: boolean;
    is_deleted: boolean;
    last_edited: string;
    created_at: string;
    content: Record<string, unknown> | null;
}

export interface BoardInsert {
    id: string;
    user_id: string;
    title: string;
    description?: string;
    slug: string;
    icon?: string;
    theme_color?: string;
    is_favorite?: boolean;
    is_deleted?: boolean;
    content?: Record<string, unknown>;
}

export interface BoardUpdate {
    title?: string;
    description?: string;
    slug?: string;
    icon?: string;
    theme_color?: string;
    is_favorite?: boolean;
    is_deleted?: boolean;
    last_edited?: string;
    content?: Record<string, unknown>;
}

// Extended types for queries with relations
export interface PageWithNoteCount {
    id: string;
    notes: Array<{ count: number }>;
}

export interface PageWithNotes {
    id: string;
    notes: Array<{
        id: string;
        page_id: string;
        type: string;
        content: string | null;
        x: number;
        y: number;
        width: number;
        height: number;
        color_theme: string | null;
        meta: Record<string, unknown> | null;
        created_at: string;
        updated_at: string;
    }>;
}

export interface BoardRowWithPages extends BoardRow {
    pages?: PageWithNoteCount[] | PageWithNotes[];
}

export interface ProfileData {
    email?: string;
    full_name?: string;
    avatar_url?: string;
}

// =============================================================================
// Repository
// =============================================================================

export const boardRepository = {
    /**
     * Find all boards for current user
     */
    async findAll(): Promise<BoardRow[]> {
        logger.debug('[BoardRepo] findAll');

        const { data, error } = await supabase
            .from('boards')
            .select('*, pages(notes(count))')
            .order('is_favorite', { ascending: false })
            .order('last_edited', { ascending: false });

        if (error) {
            throw new ApiError('Failed to fetch boards', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Find all boards with full content (pages and notes)
     * Used for Smart View source picker to access all available notes
     */
    async findAllWithContent(): Promise<BoardRow[]> {
        logger.debug('[BoardRepo] findAllWithContent');

        const { data, error } = await supabase
            .from('boards')
            .select(`
                *,
                pages (
                    *,
                    notes (*)
                )
            `)
            .order('last_edited', { ascending: false });

        if (error) {
            throw new ApiError('Failed to fetch boards with content', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Find board by ID
     */
    async findById(id: string): Promise<BoardRow | null> {
        logger.debug('[BoardRepo] findById', { id });

        const { data, error } = await supabase
            .from('boards')
            .select('*, pages(notes(count))')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null;
            }
            throw new ApiError('Failed to fetch board', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Find board by slug
     */
    async findBySlug(slug: string): Promise<BoardRow | null> {
        logger.debug('[BoardRepo] findBySlug', { slug });

        const { data, error } = await supabase
            .from('boards')
            .select('*, pages(notes(count))')
            .eq('slug', slug)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null;
            }
            throw new ApiError('Failed to fetch board by slug', {
                statusCode: 500,
                context: { slug, error: error.message },
            });
        }

        return data;
    },

    /**
     * Create a new board
     */
    async create(board: BoardInsert): Promise<BoardRow> {
        logger.debug('[BoardRepo] create', { title: board.title });

        const { data, error } = await supabase
            .from('boards')
            .insert({
                ...board,
                created_at: new Date().toISOString(),
                last_edited: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to create board', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data;
    },

    /**
     * Update a board
     */
    async update(id: string, updates: BoardUpdate): Promise<BoardRow> {
        logger.debug('[BoardRepo] update', { id, fields: Object.keys(updates) });

        const { data, error } = await supabase
            .from('boards')
            .update({
                ...updates,
                last_edited: new Date().toISOString(),
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to update board', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Delete a board
     */
    async delete(id: string): Promise<void> {
        logger.debug('[BoardRepo] delete', { id });

        const { error } = await supabase
            .from('boards')
            .delete()
            .eq('id', id);

        if (error) {
            throw new ApiError('Failed to delete board', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }
    },

    /**
     * Check if slug exists for user
     */
    async slugExists(slug: string, excludeId?: string): Promise<boolean> {
        const userId = getCurrentUserId();
        if (!userId) return false;

        let query = supabase
            .from('boards')
            .select('id', { count: 'exact', head: true })
            .eq('slug', slug)
            .eq('user_id', userId);

        if (excludeId) {
            query = query.neq('id', excludeId);
        }

        const { count } = await query;
        return (count ?? 0) > 0;
    },
};
